import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Layout } from 'components/common/layout/Layout';
import {
    Espin_Follow_Type_Enum,
    Espin_Player_Profile_Bool_Exp,
    Espin_Player_Profile_Order_By,
    Espin_Role_Enum,
    GetCurrentCollegeUserLocationDocument,
    GetCurrentCollegeUserLocationQuery,
    GetCurrentCollegeUserLocationQueryVariables,
    Order_By,
    PlayerCardFragment,
    Players_IndexDocument,
    SearchPlayersDocument,
    SearchPlayersQuery,
    SearchPlayersQueryVariables,
} from 'generated/spin-graphql';
import { PageHeader } from '../../components/sections/PageHeader';
import { PageListHeader } from '../../components/sections/PageListHeader';

import {
    createSearchBarProvider,
    SearchActionType,
    SearchBarContext,
    SearchBarContextType,
    useAuthenticatedQuery,
} from '../../client/hooks';
import { WritableDraft } from 'immer/dist/internal';
import { gql, useClient, useQuery } from 'urql';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppState } from '../../client/providers/AppProvider';
import { mixpanelRecordEvent } from 'components/analytics/analyticsWrapper';

import { useImmer } from 'use-immer';
import {
    FieldOfStudyToggleType,
    PlayerSearchFilterComponent,
    PlayerSearchFilterState,
    PlayerSorting,
} from '../../components/players/search/PlayerSearchFilterComponent';
import { UrqlSSGForPage } from '../../server/utils/serverUtils/ssg.utils';
import { CustomSEO } from '../../components/common/layout/HeadMeta';
import { usePlayerSearchHistoryProvider } from '../../client/providers/search/PlayerSearchHistoryProvider';
import { PlayerListDisplay } from '../../components/players/search/PlayerSearchResultsDisplay';
import { MixpanelEvents } from '../../shared/shared.types';
import { CONVERT_TO_MILES, PLAYER_PAGE_SIZE } from '../../shared/shared.constants';

gql`
    query Players_Index {
        ...PlayerSearchFilter
    }

    fragment PlayerSearchFilter on query_root {
        espin_game_definition {
            display_name
            game_name
        }
        espin_study_field {
            ...PlayerAcademicInterestsStudyFields
        }
        espin_study_subject {
            ...PlayerAcademicInterestsStudySubjects
        }
    }

    query SearchPlayers(
        $search: espin_player_profile_bool_exp!
        $offset: Int! = 0
        $pageSize: Int! = 50
        $orderBy: [espin_player_profile_order_by!] = { spin_username: asc }
    ) {
        espin_player_profile(where: $search, order_by: $orderBy, limit: $pageSize, offset: $offset) {
            id
            ...PlayerListItem
            ...PlayerCard
        }
        espin_player_profile_aggregate(where: $search, order_by: $orderBy) {
            aggregate {
                count
            }
        }
    }

    query GetCurrentCollegeUserLocation($id: uuid!) {
        espin_college_by_pk(id: $id) {
            address {
                location
            }
        }
    }
`;

export default function Players_Index() {
    const { getAccessTokenSilently } = useAuth0();
    const { user, subscribed } = useAppState();

    // we don't _need_ to be logged in here, all public (altho client side) data
    const [result] = useQuery({ query: Players_IndexDocument });

    // since an update can happen in the same session, and location updates are async,
    // we pull in the location immediately
    const { data: locationData } = useAuthenticatedQuery<
        GetCurrentCollegeUserLocationQuery,
        GetCurrentCollegeUserLocationQueryVariables
    >(
        {
            query: GetCurrentCollegeUserLocationDocument,
            variables: {
                id: user?.college_profile?.college.id,
            },
            pause: !user?.college_profile?.college?.id,
        },
        ['espin_address'],
    );

    const SearchBarProvider = useMemo(() => createSearchBarProvider(SearchBarContext), []);
    const searchHistory = usePlayerSearchHistoryProvider();

    const [hasSearched, setHasSearched] = useState<boolean>(false);

    const pageDescription = `
    Find top talent from across the world! Search for players by game, GPA, 
    and location (more search options coming soon!) and connect with esports 
    players who are interested in competing at the collegiate level. Click 
    on player listing to find an extensive profile with recruiting information 
    from inside and outside the game, ensuring you find players with 
    the best fit for your collegiate program.
    `;

    const seoOverride: CustomSEO = {
        title: 'Players',
        description: pageDescription,
    };

    const gqlClient = useClient();

    const [currentFilterDetails, setCurrentFilterDetails] = useImmer<PlayerSearchFilterState>({
        ...searchHistory.playerFilters,
    });

    // will feed in from the search filters state
    const buildSearchCriteria = useCallback(
        (searchText: string, applyFilters: boolean) => {
            let termSearch: Espin_Player_Profile_Bool_Exp | null = null;
            if (searchText !== '') {
                termSearch = {
                    _or: [
                        { first_name: { _ilike: `%${searchText}%` } },
                        { last_name: { _ilike: `%${searchText}%` } },
                        { spin_username: { _ilike: `%${searchText}%` } },
                        { player_games: { rank: { _ilike: `%${searchText}%` } } },
                        { player_games: { in_game_username: { _ilike: `%${searchText}%` } } },
                    ],
                };
            }

            let distanceSearch: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.useMaxDistance) {
                distanceSearch = {
                    address: {
                        location: {
                            _st_d_within: {
                                distance: currentFilterDetails.maxDistance * CONVERT_TO_MILES,
                                from: {
                                    type: 'Point',
                                    coordinates: locationData?.espin_college_by_pk?.address?.location?.coordinates ?? [
                                        0,
                                        0,
                                    ],
                                },
                            },
                        },
                    },
                };
            }

            let gpaSearch: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.useGPA) {
                gpaSearch = {
                    gpa: { _gte: currentFilterDetails.minGPA },
                };
            }
            let gradClass: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.useGraduatingClass && currentFilterDetails.graduatingClass.length > 0) {
                const values = currentFilterDetails.graduatingClass.map((a) => a['value'].toString());
                if (values.includes('Other')) {
                    //remove other from all options
                    const arrCp = [...currentFilterDetails.classOptions]; //cp
                    arrCp.splice(
                        currentFilterDetails.classOptions.findIndex((a) => a.value === 'Other'),
                        1,
                    );
                    // not necessary to remove item from exclude if we are including it
                    const exclude = arrCp.map((a) => a['value'].toString());
                    //remove other from user selection
                    const include = [...values];
                    include.splice(include.indexOf('Other'), 1);
                    gradClass = {
                        _or: [{ graduating_class: { _in: include } }, { graduating_class: { _nin: exclude } }],
                    };
                } else {
                    gradClass = {
                        graduating_class: { _in: values },
                    };
                }
            }
            let state: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.useState) {
                state = {
                    address: { state: { _eq: currentFilterDetails.state } },
                };
            }
            let internationalSearch: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.useInternationalSearch) {
                internationalSearch = {
                    address: { country: { _neq: 'United States' } },
                };
            }

            let playersIFollow: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.usePlayersIFollow) {
                playersIFollow = {
                    user: {
                        player_user_follows: {
                            college_id: {
                                _eq: user.college_profile.college.id,
                            },
                            follow_type: {
                                _eq: Espin_Follow_Type_Enum.CollegeFollowsPlayer,
                            },
                        },
                    },
                };
            }

            let playersIDoNotFollow: Espin_Player_Profile_Bool_Exp | null = null;
            if (currentFilterDetails.usePlayersIDoNotFollow) {
                playersIDoNotFollow = {
                    user: {
                        _not: {
                            player_user_follows: {
                                college_id: {
                                    _eq: user.college_profile.college.id,
                                },
                                follow_type: {
                                    _eq: Espin_Follow_Type_Enum.CollegeFollowsPlayer,
                                },
                            },
                        },
                    },
                };
            }

            let sorting: Espin_Player_Profile_Order_By | null = null;

            if (currentFilterDetails.useSorting) {
                switch (currentFilterDetails.sorting) {
                    case PlayerSorting.LOCATION:
                        sorting = {
                            address: { location: Order_By.Desc },
                        };
                        break;

                    case PlayerSorting.GRADUATING_CLASS:
                        sorting = {
                            graduating_class: Order_By.Asc,
                        };
                        break;
                }
            }

            let simpleFieldOfStudy: Espin_Player_Profile_Bool_Exp | null = null;
            if (
                currentFilterDetails.useFilterByFieldOfStudy &&
                currentFilterDetails.fieldOfStudyType === FieldOfStudyToggleType.SIMPLE
            ) {
                simpleFieldOfStudy = {
                    general_study_field: {
                        name: {
                            _eq: currentFilterDetails.fieldOfStudy.value,
                        },
                    },
                };
            }

            let advancedFieldOfStudy: Espin_Player_Profile_Bool_Exp | null = null;
            if (
                currentFilterDetails.useFilterByFieldOfStudy &&
                currentFilterDetails.fieldOfStudyType === FieldOfStudyToggleType.ADVANCED
            ) {
                const generalStudies = currentFilterDetails.advancedFieldOfStudy.filter((v) => v.indexOf('|') === -1);
                const paired = currentFilterDetails.advancedFieldOfStudy.filter((v) => v.includes('|'));
                let subjects = paired.map((v) => v.split('|')?.[1]?.trim());
                let subjects_study_fields = paired.map((v) => v.split('|')?.[0]?.trim());
                subjects = subjects.filter((s) => s !== undefined);
                subjects_study_fields = subjects_study_fields.filter((s) => s !== undefined);

                let andQuery = [];
                for (let i = 0; i < subjects.length; i++) {
                    andQuery.push({
                        _and: [
                            {
                                subject_study_field: {
                                    name: {
                                        _eq: subjects[i],
                                    },
                                },
                            },
                            {
                                general_study_field: {
                                    name: {
                                        _eq: subjects_study_fields[i],
                                    },
                                },
                            },
                        ],
                    });
                }

                const orQuery = [];
                orQuery.push({ general_study_field: { name: { _in: generalStudies } } });

                if (andQuery.length > 0 && andQuery) orQuery.push({ _or: [...andQuery] });
                advancedFieldOfStudy = {
                    _or: [...orQuery],
                };
            }

            let gamesSearch: Espin_Player_Profile_Bool_Exp | null = null;

            if (
                currentFilterDetails.useGames &&
                currentFilterDetails.gameFilters &&
                currentFilterDetails.gameFilters.length > 0
            ) {
                gamesSearch = {
                    player_games: {
                        primary: { _eq: true },
                        game: {
                            game_definition: {
                                _in: currentFilterDetails.gameFilters,
                            },
                        },
                    },
                };
            }

            // build query
            const base: Espin_Player_Profile_Bool_Exp = {
                _and: [
                    {
                        user: {
                            deleted_at: {
                                _is_null: true,
                            },
                        },
                    },
                    termSearch && termSearch,
                    gpaSearch && gpaSearch,
                    gradClass && gradClass,
                    state && state,
                    advancedFieldOfStudy && advancedFieldOfStudy,
                    simpleFieldOfStudy && simpleFieldOfStudy,
                    playersIFollow && playersIFollow,
                    playersIDoNotFollow && playersIDoNotFollow,
                    internationalSearch && internationalSearch,
                    distanceSearch && distanceSearch,
                    gamesSearch && gamesSearch,
                ].filter((s) => !!s),
            };

            if (currentFilterDetails.useSorting) {
                return { base, sorting };
            }

            return { base };
            // update this callback w/ filter data
        },
        [currentFilterDetails, user, locationData],
    );

    const canSearch = useMemo(() => subscribed && user?.role === Espin_Role_Enum.EspinCollegeUser, [
        subscribed,
        user?.role,
    ]);

    const searchPlayers = useCallback(
        async (s: string, filtersActive: boolean, offset: number = 0, isLoadMore: boolean = false) => {
            setHasSearched(true);
            //we save these if we do the load more stuff.

            if (!canSearch) {
                // if i'm not a subbed college, do not send the search

                return {
                    results: [],
                    count: 0,
                };
            }
            // call our search function since it's async!
            // i need ot get our filter data here so i can build me query. :D

            // get our access token, we need to be logged in
            const accessToken = await getAccessTokenSilently();

            const criteria = buildSearchCriteria(s, filtersActive);

            const r = await gqlClient
                .query<SearchPlayersQuery, SearchPlayersQueryVariables>(
                    SearchPlayersDocument,
                    { search: criteria.base, offset, pageSize: PLAYER_PAGE_SIZE, orderBy: criteria.sorting },
                    {
                        requestPolicy: 'network-only',
                        fetchOptions: {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        },
                    },
                )
                .toPromise();

            mixpanelRecordEvent({
                event: MixpanelEvents.PLAYER_SEARCH,
                properties: {
                    distinct_id: user?.id,
                    timestamp: new Date().toISOString(),
                    user_role: user?.role,
                    user_id: user?.id,
                    gpa_input: currentFilterDetails.useGPA ? currentFilterDetails.minGPA : null,
                    distance_input: currentFilterDetails.useMaxDistance ? currentFilterDetails.maxDistance : null,
                    graduating_class_input: currentFilterDetails.graduatingClass.map((a) => a.value),
                    state_input: currentFilterDetails.state,
                    international_search: currentFilterDetails.useInternationalSearch,
                    simple_field_of_study: currentFilterDetails.fieldOfStudy.value,
                    advanced_field_of_study: currentFilterDetails.advancedFieldOfStudy,
                    games_filter_input: currentFilterDetails.gameFilters,
                    sort_input: currentFilterDetails.useSorting ? currentFilterDetails.sorting['value'] : null,
                    show_more: isLoadMore,
                },
            });

            if (!!r.error) {
                toast.error('Error in search');
                searchHistory.setPlayerSearchHistory((draft) => {
                    draft.previousSearchCount = 0;
                    draft.previousSearchData = [];
                });

                return {
                    results: [],
                    count: 0,
                };
            }

            searchHistory.setPlayerSearchHistory((draft) => {
                draft.playerFilters = {
                    useGames: currentFilterDetails.useGames,
                    useFilterByFieldOfStudy: currentFilterDetails.useFilterByFieldOfStudy,
                    fieldOfStudyType: currentFilterDetails.fieldOfStudyType,
                    gameFilters: currentFilterDetails.gameFilters,
                    fieldOfStudy: currentFilterDetails.fieldOfStudy,
                    useMaxDistance: currentFilterDetails.useMaxDistance,
                    useInternationalSearch: currentFilterDetails.useInternationalSearch,
                    useGPA: currentFilterDetails.useGPA,
                    advancedFieldOfStudy: currentFilterDetails.advancedFieldOfStudy,
                    useGraduatingClass: currentFilterDetails.useGraduatingClass,
                    useSorting: currentFilterDetails.useSorting,
                    useState: currentFilterDetails.useState,
                    maxDistance: currentFilterDetails.maxDistance,
                    minGPA: currentFilterDetails.minGPA,
                    // useFollowFilter: currentFilterDetails.useFollowFilter,
                    usePlayersIDoNotFollow: currentFilterDetails.usePlayersIDoNotFollow,
                    usePlayersIFollow: currentFilterDetails.usePlayersIFollow,
                    graduatingClass: currentFilterDetails.graduatingClass,
                    sorting: currentFilterDetails.sorting,
                    state: currentFilterDetails.state,
                };
                draft.playerSearch = s;
                draft.previousSearchCount = r.data.espin_player_profile_aggregate.aggregate.count;

                draft.previousSearchData = isLoadMore
                    ? [...draft.previousSearchData, ...r.data.espin_player_profile]
                    : r.data.espin_player_profile;

                draft.previousSearchOffset = isLoadMore ? draft.previousSearchOffset + PLAYER_PAGE_SIZE : 0;
            });

            return {
                results: r.data.espin_player_profile,
                count: r.data.espin_player_profile_aggregate.aggregate.count,
            };
        },
        [
            canSearch,
            getAccessTokenSilently,
            buildSearchCriteria,
            gqlClient,
            searchHistory,
            currentFilterDetails,
            user?.id,
            user?.role,
        ],
    );

    const playerSearchReducer = useCallback(function (
        draft: WritableDraft<{
            offset: number;
            hasSearched: boolean;
            searchData: PlayerCardFragment[];
            fullData: PlayerCardFragment[];
            hasMore: boolean;
            lastSearchText: string;
            lastSearchFiltersActive: boolean;
        }>,
        action: SearchActionType,
    ) {
        switch (action.type) {
            case 'asyncSearchCompleted':
                return {
                    offset: 0,
                    hasSearched: true,
                    fullData: action.results,
                    hasMore: action.totalResults > PLAYER_PAGE_SIZE,
                    searchData: action.results,
                    lastSearchText: action.lastSearchTerm,
                    lastSearchFiltersActive: action.filtersActive,
                };

            // action.results;
            case 'asyncLoadMoreCompleted':
                // will have results + previous list too?

                const newData = [...draft.searchData, ...action.results];
                return {
                    offset: action.offset,
                    hasSearched: true,
                    fullData: newData,
                    hasMore: action.totalResults > PLAYER_PAGE_SIZE + action.offset,
                    searchData: newData,
                    lastSearchText: action.lastSearchTerm,
                    lastSearchFiltersActive: action.filtersActive,
                };

            default:
                break;
        }
    }, []);

    return (
        <Layout seoOverride={seoOverride}>
            <PageHeader title={'Players'} description={pageDescription} className="players-page-header" />
            <SearchBarProvider
                initialData={{
                    fullData: searchHistory.previousSearchData,
                    hasMore: searchHistory.previousSearchCount > searchHistory.previousSearchOffset + PLAYER_PAGE_SIZE,
                    hasSearched: false,
                    offset: searchHistory.previousSearchOffset,
                    searchData: searchHistory.previousSearchData,
                    lastSearchText: searchHistory.playerSearch,
                    lastSearchFiltersActive: false,
                }}
                reducer={playerSearchReducer}
            >
                <PageListHeader
                    hasSearchButton={false}
                    className="players-page-list-header"
                    isStandaloneHeader={false}
                    title={'Search For Players'}
                    searchPlaceholder={'Search by Name'}
                    filtersAlwaysActive={true}
                    defaultValue={searchHistory.playerSearch}
                    filters={useMemo(
                        () => [
                            <PlayerSearchFilterComponent
                                defaults={searchHistory.playerFilters}
                                onChange={(d) => setCurrentFilterDetails(d)}
                                data={result.data}
                                key="player-search"
                            />,
                        ],
                        [result.data, searchHistory.playerFilters, setCurrentFilterDetails],
                    )}
                    customAsyncSearch={searchPlayers}
                />

                <PlayerListDisplay searchPlayers={searchPlayers} />
            </SearchBarProvider>
        </Layout>
    );
}

export async function getStaticProps(ctx) {
    return UrqlSSGForPage(Players_IndexDocument);
}

export function usePlayersSearchContext(): SearchBarContextType<PlayerCardFragment> {
    return useContext(SearchBarContext);
}
